import { NoteChange } from "@/data/models/ProjectNote";
// Type-only imports: `ProjectDetails` and `ProjectState` pull in repositories and store
// modules, and importing either at runtime would drag the whole app into any test that
// touches this file. The enum value is passed in by the caller for the same reason.
import type { ProjectSyncTarget } from "@/data/models/ProjectDetails";
import type { ProjectStatusSyncStatus } from "@/data/models/ProjectState";

/**
 * Shape of the merge payload written to
 * `clients/{clientId}/projects/{projectId}/syncedNotes/data`.
 *
 * The document holds parallel maps: `notes` keyed by internal note id, `externalNoteIds`
 * mapping that internal id to the external system's own id, and `pendingSyncs` tracking what
 * still has to be pushed to each sync target.
 */
export interface SyncedNoteUpdate {
  notes: Record<string, unknown | null>;
  externalNoteIds?: Record<string, string>;
  pendingSyncs?: Record<string, unknown>;
}

/**
 * The external note id IRES Office invents for a note it created itself: the creation
 * timestamp in whole seconds.
 *
 * IRES Cloud stores the *partner's* own id under the same key for notes that arrived through
 * `PUT /external/v1/note`, so this value must only ever be written when Office is creating
 * the note. Overwriting a partner's id would make Cloud's `getInternalNoteId` fail to match
 * on the next sync, mint a fresh internal id, and duplicate the note.
 *
 * Returns `undefined` when no timestamp is available, so the caller omits the entry rather
 * than throwing — the old inline code dereferenced this path unguarded.
 */
function deriveExternalNoteId(
  projectNoteLog: { createdBy?: { recordedAt?: { seconds?: number } } } | null | undefined
): string | undefined {
  const seconds = projectNoteLog?.createdBy?.recordedAt?.seconds;

  return seconds === undefined ? undefined : seconds.toString();
}

/**
 * Builds the merge payload for a synced-note write.
 *
 * Extracted as a pure function because both writers of the document need identical
 * semantics: `ProjectNotesRepository.updateNote` and
 * `ProjectLogRepository.saveSyncedProjectNote`. Keeping the rules in one place is what stops
 * the two drifting apart, which is how the `externalNoteIds` invariant was lost in the first
 * place (IRES Cloud audit finding F-10).
 *
 * Rules:
 * - `notes[noteId]` is the note, or `null` as a tombstone on deletion.
 * - `externalNoteIds[noteId]` is written **only on creation** — see `deriveExternalNoteId`.
 * - `pendingSyncs[noteId]` is written only when the project has a sync target, marking every
 *   one of its ids as needing a sync.
 *
 * The result is written with `set(..., { merge: true })`, so it must only ever contain keys
 * for this one note.
 *
 * @param noteId Internal note id.
 * @param projectNoteLog The note being written, or `null` when deleting.
 * @param noteChange Whether the note is being created, updated or deleted.
 * @param pendingSyncStatus Status to record per sync target, normally
 *   `ProjectStatusSyncStatus.NEEDS_SYNC`. Passed in so this module stays free of runtime
 *   imports from `ProjectState`.
 * @param projectSyncTarget The project's sync target, when it has one.
 */
export function buildSyncedNoteUpdate(
  noteId: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  projectNoteLog: any,
  noteChange: NoteChange,
  pendingSyncStatus: ProjectStatusSyncStatus,
  projectSyncTarget?: ProjectSyncTarget
): SyncedNoteUpdate {
  const update: SyncedNoteUpdate = {
    notes: {
      [noteId]: noteChange === NoteChange.DELETED ? null : projectNoteLog,
    },
  };

  if (projectSyncTarget) {
    const syncedWith: Record<string, ProjectStatusSyncStatus> = {};

    projectSyncTarget.syncTargetIds.forEach(
      (syncTargetId) => (syncedWith[syncTargetId] = pendingSyncStatus)
    );

    // `filterOutUndefined` was applied here previously. Both keys are always defined —
    // `noteChange` is required and `syncedWith` is always an object — so it was a no-op, and
    // dropping it keeps this module free of the heavy `@/utils/Utils` import.
    update.pendingSyncs = {
      [noteId]: {
        noteChange: noteChange,
        syncedWith: syncedWith,
      },
    };
  }

  if (noteChange === NoteChange.CREATED) {
    const externalNoteId = deriveExternalNoteId(projectNoteLog);

    if (externalNoteId !== undefined) {
      update.externalNoteIds = { [noteId]: externalNoteId };
    }
  }

  return update;
}
